/**
 * Working inside a branch from the UI.
 *
 * A branch is a sibling tenant that release-service forks from main. The MCP
 * works in one with `checkout_branch`; the UI does it here: while a branch is
 * active, every request the platform services receive names the branch tenant
 * in `x-tenant-id` (and in a `tenant_id` query param that named main), so all
 * reads and writes land in the branch. The backend accepts it for a session
 * whose token is for that branch's main (shared/branch_access.py).
 *
 * Auth-service and release-service always keep main: identity, users and
 * permissions live in main, and branches and change requests are addressed
 * by name under main.
 *
 * The choice lives in sessionStorage (per tab), and switching reloads the
 * page so no store keeps main's data on screen as if it were the branch's.
 */
import { useSyncExternalStore } from 'react';
import { getTenantId } from '../store/authStore';

export interface ActiveBranch {
  name: string;
  tenant_id: string;
  main_tenant_id: string;
}

const STORAGE_KEY = 'nexus.activeBranch';
const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';
const RELEASE_API = import.meta.env.VITE_RELEASE_SERVICE_URL || 'http://localhost:8036';

/** Prefix of release_write_guard.PROTECTED_MAIN_DETAIL (backend). */
export const PROTECTED_MAIN_PREFIX = 'main está protegida';
/** Window event fired when a write was refused because main is protected. */
export const PROTECTED_MAIN_EVENT = 'nexus:protected-main';

function load(): ActiveBranch | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const b = JSON.parse(raw) as ActiveBranch;
    return b && b.name && b.tenant_id && b.main_tenant_id ? b : null;
  } catch {
    return null;
  }
}

let current: ActiveBranch | null = load();
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The active branch for the signed-in tenant, or null when working in main. */
export function getActiveBranch(): ActiveBranch | null {
  if (!current) return null;
  // A branch chosen under another session's tenant never applies.
  return current.main_tenant_id === getTenantId() ? current : null;
}

export function setActiveBranch(branch: ActiveBranch | null): void {
  current = branch;
  try {
    if (branch) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(branch));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode: keep it in memory */ }
  listeners.forEach(fn => fn());
}

/** Switch and reload, so every module loads its data from the new tenant. */
export function switchBranch(branch: ActiveBranch | null, reload: () => void = () => window.location.reload()): void {
  setActiveBranch(branch);
  reload();
}

export function useActiveBranch(): ActiveBranch | null {
  useSyncExternalStore(subscribe, () => current, () => current);
  return getActiveBranch();
}

function absolute(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost/').href;
  } catch {
    return url;
  }
}

/** Requests that must keep main even inside a branch. */
export function keepsMain(url: string): boolean {
  const target = absolute(url);
  return [AUTH_API, RELEASE_API].some(base => base && target.startsWith(absolute(base)));
}

/** The tenant a request should carry: the branch when it named main. */
export function tenantForRequest(url: string, tenant: string): string {
  const branch = getActiveBranch();
  if (!branch || tenant !== branch.main_tenant_id || keepsMain(url)) return tenant;
  return branch.tenant_id;
}

/** Rewrite a `tenant_id=<main>` query param to the branch. Other URLs pass untouched. */
export function urlForRequest(url: string): string {
  const branch = getActiveBranch();
  if (!branch || !url.includes('tenant_id=') || keepsMain(url)) return url;
  try {
    const u = new URL(url, window.location.href);
    if (u.searchParams.get('tenant_id') !== branch.main_tenant_id) return url;
    u.searchParams.set('tenant_id', branch.tenant_id);
    return u.href;
  } catch {
    return url;
  }
}

export function isProtectedMainMessage(message: unknown): boolean {
  return typeof message === 'string' && message.includes(PROTECTED_MAIN_PREFIX);
}

export function announceProtectedMain(message: string): void {
  window.dispatchEvent(new CustomEvent(PROTECTED_MAIN_EVENT, { detail: { message } }));
}

/** Look at a 403 without consuming it; announce it when main refused the write. */
export function inspectRefusal(resp: Response): void {
  if (resp.status !== 403) return;
  const tagged = resp.headers.get('x-nexus-refusal') === 'protected_branch';
  resp.clone().json().then((body: { detail?: unknown }) => {
    const detail = body?.detail;
    if (tagged || isProtectedMainMessage(detail)) {
      announceProtectedMain(typeof detail === 'string' ? detail : PROTECTED_MAIN_PREFIX);
    }
  }).catch(() => undefined);
}
